import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useOnboarding } from '@/contexts/OnboardingContext';

const BUBBLE_WIDTH = 320;
const BUBBLE_EST_HEIGHT = 200;
const VIEWPORT_MARGIN = 16;

interface BubblePosition {
  top: number;
  left: number;
  placement: 'below' | 'above';
}

export function OnboardingTour() {
  const { steps, stepIndex, isActive, next, back, dismiss, complete } = useOnboarding();
  const step = steps[stepIndex];
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [bubblePos, setBubblePos] = useState<BubblePosition | null>(null);

  const isModal = !step?.selector;
  const [listboxRect, setListboxRect] = useState<DOMRect | null>(null);

  // Locate the target element for anchored steps. Content loads async, so poll
  // briefly; if it never appears (e.g. page not open), skip to the next step.
  useEffect(() => {
    if (!isActive || !step?.selector) {
      setTargetRect(null);
      setListboxRect(null);
      return;
    }

    const selector = `[data-tour="${step.selector}"]`;
    const isSelectStep = step.advance === 'select';
    let found = false;
    // Clear the previous step's spotlight so a missing target never leaves the
    // highlight sitting on the wrong element.
    setTargetRect(null);
    setListboxRect(null);

    const measure = () => {
      const el = document.querySelector(selector);
      if (!el) return false;
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return false;
      setTargetRect(rect);
      // On select steps, also spotlight the open dropdown list (rendered in a
      // portal) so the user sees the options are part of the highlight.
      if (isSelectStep) {
        const listbox = document.querySelector('[role="listbox"]');
        if (listbox) {
          const lRect = listbox.getBoundingClientRect();
          setListboxRect(lRect.width > 0 && lRect.height > 0 ? lRect : null);
        } else {
          setListboxRect(null);
        }
      }
      return true;
    };

    const startedAt = Date.now();
    const poll = window.setInterval(() => {
      if (!found) {
        const el = document.querySelector(selector);
        if (el) {
          el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
        found = measure();
        // Some areas only exist once a collection/style/source is selected.
        // Rather than stalling on an empty spotlight, move to the next step.
        if (!found && step.skipIfMissing && Date.now() - startedAt > 2500) {
          window.clearInterval(poll);
          next();
        }
      } else {
        measure();
      }
    }, 120);

    const update = () => measure();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);

    return () => {
      window.clearInterval(poll);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [isActive, stepIndex, step?.selector, step?.skipIfMissing, step?.advance, next]);

  // Position the bubble relative to the spotlight target.
  useEffect(() => {
    if (!targetRect || isModal) {
      setBubblePos(null);
      return;
    }
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Steps flagged side: 'right' keep the bubble beside the target so it
    // never covers an open dropdown list that drops below the control.
    if (step?.side === 'right') {
      const left = Math.min(targetRect.right + 16, vw - BUBBLE_WIDTH - VIEWPORT_MARGIN);
      const top = Math.min(
        Math.max(targetRect.top, VIEWPORT_MARGIN),
        vh - BUBBLE_EST_HEIGHT - VIEWPORT_MARGIN,
      );
      setBubblePos({ top, left, placement: 'below' });
      return;
    }

    const spaceBelow = vh - targetRect.bottom;
    const placement: 'below' | 'above' =
      spaceBelow >= BUBBLE_EST_HEIGHT + VIEWPORT_MARGIN ? 'below' : 'above';

    const top = placement === 'below'
      ? Math.min(targetRect.bottom + 12, vh - BUBBLE_EST_HEIGHT - VIEWPORT_MARGIN)
      : Math.max(targetRect.top - BUBBLE_EST_HEIGHT - 12, VIEWPORT_MARGIN);

    const centered = targetRect.left + targetRect.width / 2 - BUBBLE_WIDTH / 2;
    const left = Math.min(
      Math.max(centered, VIEWPORT_MARGIN),
      vw - BUBBLE_WIDTH - VIEWPORT_MARGIN,
    );

    setBubblePos({ top, left, placement });
  }, [targetRect, isModal, step?.side]);

  // Anchored steps advance only when the user actually clicks the expected
  // element. Use clickSelector if provided so a large highlighted area can still
  // require a specific button press. Listen in the capture phase so the app's
  // own handler still runs.
  useEffect(() => {
    if (!isActive || !step?.selector) return;
    const mode = step.advance ?? 'click';
    if (mode === 'next') return;
    const clickSelector = `[data-tour="${step.clickSelector ?? step.selector}"]`;
    // Several detectors (click, pointerup, mutation) can fire for one single
    // choice. Only the first one may advance, otherwise the tour skips a step.
    let advanced = false;
    let timer: number | undefined;
    const advanceOnce = () => {
      if (advanced) return;
      advanced = true;
      timer = window.setTimeout(() => next(), 350);
    };
    const onClick = (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (mode === 'select') {
        // Dropdown options render in a portal, outside the highlighted element.
        if (!target?.closest?.('[role="option"]')) return;
      } else if (!target?.closest?.(clickSelector)) {
        return;
      }
      advanceOnce();
    };
    document.addEventListener('click', onClick, true);
    // Radix Select commits a choice on pointerup/keyboard, which does not always
    // produce a click event, so listen for those too on select steps.
    if (mode === 'select') {
      document.addEventListener('pointerup', onClick, true);
    }

    // Safety net: if the trigger's displayed value changes, the user picked
    // something even if we missed the event.
    let observer: MutationObserver | undefined;
    if (mode === 'select') {
      const el = document.querySelector(`[data-tour="${step.selector}"]`);
      if (el) {
        const initial = el.textContent ?? '';
        observer = new MutationObserver(() => {
          if ((el.textContent ?? '') !== initial) {
            observer?.disconnect();
            advanceOnce();
          }
        });
        observer.observe(el, { childList: true, subtree: true, characterData: true });
      }
    }

    return () => {
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('pointerup', onClick, true);
      observer?.disconnect();
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [isActive, stepIndex, step?.selector, step?.clickSelector, step?.advance, next]);


  // Escape dismisses the tour.
  useEffect(() => {
    if (!isActive) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isActive, dismiss]);

  const handlePrimary = useCallback(() => {
    if (stepIndex === steps.length - 1) {
      complete();
    } else {
      next();
    }
  }, [stepIndex, next, complete]);

  if (!isActive || !step) return null;

  const isLast = stepIndex === steps.length - 1;
  const isWelcome = step.id === 'welcome';
  const advanceMode = step.advance ?? 'click';
  const isExplainer = !isModal && advanceMode === 'next';

  const card = (
    <div className="bg-popover text-popover-foreground rounded-xl border border-border shadow-2xl p-5 relative">
      <button
        type="button"
        onClick={dismiss}
        aria-label="Close tour"
        className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
      <h3 className="font-serif text-lg font-bold pr-6">{step.title}</h3>
      <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{step.body}</p>
      {!isModal && !isExplainer && (
        <p className="text-xs font-medium text-primary mt-3">
          {advanceMode === 'select'
            ? 'Pick an option in the highlighted list to continue.'
            : 'Click the highlighted element to continue.'}
        </p>
      )}
      <div className="flex items-center justify-between mt-4 gap-2">
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {stepIndex + 1} of {steps.length}
        </span>
        <div className="flex items-center gap-2">
          {isWelcome ? (
            <>
              <Button variant="ghost" size="sm" onClick={dismiss}>Skip</Button>
              <Button size="sm" onClick={handlePrimary}>Start tour</Button>
            </>
          ) : isModal || isExplainer ? (
            <>
              {stepIndex > 1 && (
                <Button variant="ghost" size="sm" onClick={back} className="px-2">
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Back
                </Button>
              )}
              <Button size="sm" onClick={handlePrimary}>
                {isLast ? 'Finish' : 'Next'}
                {!isLast && <ChevronRight className="w-4 h-4 ml-1" />}
              </Button>
            </>
          ) : (
            stepIndex > 1 && (
              <Button variant="ghost" size="sm" onClick={back} className="px-2">
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(
    <div data-onboarding-tour="true" className="fixed inset-0 z-[90] pointer-events-none" role="dialog" aria-label="Guided tour">
      {isModal ? (
        <>
          <div className="absolute inset-0 bg-black/55 pointer-events-auto" />
          <div className="absolute inset-0 flex items-center justify-center p-4 pointer-events-none">
            <div className="pointer-events-auto w-full max-w-sm">{card}</div>
          </div>
        </>
      ) : (
        <>
          {/* Spotlight: the box-shadow dims everything except the cutout over
              the target. Purely visual — pointer-events stays off so the user
              can click the highlighted element itself. */}
          {targetRect && (() => {
            // Include the open dropdown list in the spotlight on select steps.
            const r = listboxRect
              ? {
                  top: Math.min(targetRect.top, listboxRect.top),
                  left: Math.min(targetRect.left, listboxRect.left),
                  right: Math.max(targetRect.right, listboxRect.right),
                  bottom: Math.max(targetRect.bottom, listboxRect.bottom),
                }
              : { top: targetRect.top, left: targetRect.left, right: targetRect.right, bottom: targetRect.bottom };
            return (
              <div
                className="absolute rounded-lg transition-all duration-200"
                style={{
                  top: r.top - 6,
                  left: r.left - 6,
                  width: r.right - r.left + 12,
                  height: r.bottom - r.top + 12,
                  boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.35)',
                  outline: '2px solid hsl(var(--primary))',
                }}
              />
            );
          })()}
          <div
            className={
              window.innerWidth < 640
                ? 'absolute inset-x-4 bottom-4 pointer-events-auto'
                : 'absolute transition-all duration-200 pointer-events-auto'
            }
            style={
              window.innerWidth >= 640
                ? {
                    width: BUBBLE_WIDTH,
                    top: bubblePos?.top ?? window.innerHeight / 2,
                    left: bubblePos?.left ?? window.innerWidth / 2 - BUBBLE_WIDTH / 2,
                  }
                : undefined
            }
          >
            {card}
          </div>
        </>
      )}
    </div>,
    document.body,
  );
}
